import { REEL_CREATOR_DEMO_MODE } from './reelCreatorConfig';
import { demoReelCreatorApi } from './reelCreatorDemoApi';
import { captureVideoStoryboard } from './videoStoryboard';

const friendlyError = async (response) => {
  const body = await response.json().catch(() => ({}));
  return body?.error || 'The Reel Creator request could not be completed.';
};

const importReferenceFile = async (reelUrl, accessToken, dashboardId) => {
  const response = await fetch(`/api/reel-creator/reference/import?dashboardId=${encodeURIComponent(dashboardId)}`, {
    method: 'POST',
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ reelUrl })
  });
  if (!response.ok) throw new Error(await friendlyError(response));
  const blob = await response.blob();
  if (!blob.size) throw new Error('The social platform returned an empty video.');
  return new File([blob], 'imported-reel.mp4', { type: blob.type || 'video/mp4' });
};

const request = async (path, { accessToken, method = 'GET', body, formData } = {}) => {
  const response = await fetch(`/api/reel-creator/${path}`, {
    method,
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(body ? { 'Content-Type': 'application/json' } : {})
    },
    body: formData || (body ? JSON.stringify(body) : undefined)
  });
  if (!response.ok) throw new Error(await friendlyError(response));
  return response.json();
};

const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

const liveApi = (accessToken, dashboardId) => {
  const getProject = async (projectId) => (await request(`projects/${projectId}?dashboardId=${encodeURIComponent(dashboardId)}`, { accessToken })).project;
  const waitForProject = async (projectId, done, timeoutMs = 180000) => {
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
      const project = await getProject(projectId);
      if (project.status === 'failed') throw new Error('Reel processing failed.');
      if (done(project)) return project;
      await wait(2000);
    }
    throw new Error('Reel processing timed out.');
  };

  return {
  listProjects: async () => (await request(`projects?dashboardId=${encodeURIComponent(dashboardId)}`, { accessToken })).projects,
  createReelProject: async () => (await request(`projects?dashboardId=${encodeURIComponent(dashboardId)}`, { accessToken, method: 'POST', body: {} })).project,
  getProject,
  uploadReference: async (projectId, { file, reelUrl }) => {
    const referenceFile = file || await importReferenceFile(reelUrl, accessToken, dashboardId);
    const formData = new FormData();
    formData.append('projectId', projectId);
    formData.append('kind', 'reference');
    formData.append('video', referenceFile);
    const project = (await request(`assets/upload?dashboardId=${encodeURIComponent(dashboardId)}`, { accessToken, method: 'POST', formData })).project;
    return { project, analysisSource: { file: referenceFile, reelUrl } };
  },
  analyseReference: async (projectId, source = {}) => {
    const storyboard = source.file ? await captureVideoStoryboard(source.file) : null;
    await request(`reference/analyse?dashboardId=${encodeURIComponent(dashboardId)}`, { accessToken, method: 'POST', body: { projectId, storyboard } });
    return waitForProject(projectId, (project) => ['filming_required', 'filming_complete'].includes(project.status));
  },
  uploadFilmedClip: async (projectId, requiredClipId, file) => {
    const formData = new FormData();
    formData.append('projectId', projectId);
    formData.append('requiredClipId', requiredClipId);
    formData.append('kind', 'filmed_clip');
    formData.append('video', file);
    return (await request(`assets/upload?dashboardId=${encodeURIComponent(dashboardId)}`, { accessToken, method: 'POST', formData })).project;
  },
  validateClip: async (projectId, requiredClipId, file) => {
    const storyboard = file ? await captureVideoStoryboard(file, { maxFrames: 8, maxWidth: 560 }) : null;
    await request(`clips/validate?dashboardId=${encodeURIComponent(dashboardId)}`, { accessToken, method: 'POST', body: { projectId, requiredClipId, storyboard } });
    const project = await waitForProject(projectId, (current) => {
      const clip = current.requiredClips?.find((item) => item.id === requiredClipId);
      return ['pass', 'retake'].includes(clip?.validation?.outcome);
    });
    return {
      project,
      validation: project.requiredClips.find((item) => item.id === requiredClipId).validation
    };
  },
  startEdit: async (projectId) => (await request(`edit/start?dashboardId=${encodeURIComponent(dashboardId)}`, { accessToken, method: 'POST', body: { projectId } })).project,
  getEditStatus: getProject,
  getFinishedVideo: async (projectId) => request(`projects/${projectId}/output?dashboardId=${encodeURIComponent(dashboardId)}`, { accessToken }),
  requestChange: async (projectId, changeRequest) => (await request(`edit/start?dashboardId=${encodeURIComponent(dashboardId)}`, { accessToken, method: 'POST', body: { projectId, changeRequest } })).project
  };
};

export const createReelCreatorApi = ({ accessToken, dashboardId = 'vasia' } = {}) => (
  REEL_CREATOR_DEMO_MODE ? demoReelCreatorApi : liveApi(accessToken, dashboardId)
);
